import orderBy from "lodash/orderBy";
import { EventWithResultAndBettingOffers, Event } from "@api-types/events";
import { EventsPerTournamentEntry } from "@api-types/events-with-results-per-tournaments";
import {
	MappedEvent,
	ResultsFormattedData,
	ResultsFormattedItem,
	ResultsFormattedTournament,
	ResultsMenuCategory,
	ResultsMenuSport,
	ResultsMenuTournament,
} from "@data-types/results";
import { PeriodType, SportAbrv } from "@utils";
import { localizationService } from "@state";
import { ScoreType } from "@common/constants";

export const BETTING_TYPES_LIST = [
	"3way",
	"winner",
	"head2head",
	"head2head-1x2",
	"winner-incl-extra-innings",
	"winner-incl-overtime",
	"winner-incl-super-over",
];

export function createMenuFromTournamentEvents(
	events: EventsPerTournamentEntry[]
) {
	const createTournament = (
		data: EventsPerTournamentEntry
	): ResultsMenuTournament => {
		return {
			id: data.tournament.id,
			sportId: data.sport.id,
			categoryId: data.sportCategory.id,
			abrv: data.tournament.abrv,
			name: data.tournament.name,
			count: data.eventCount,
			children: null,
		};
	};

	const createCategory = (
		data: EventsPerTournamentEntry
	): ResultsMenuCategory => {
		return {
			id: data.sportCategory.id,
			abrv: data.sportCategory.abrv,
			name: data.sportCategory.name,
			count: data.eventCount,
			children: [createTournament(data)],
		};
	};

	const createSport = (data: EventsPerTournamentEntry): ResultsMenuSport => {
		return {
			id: data.sport.id,
			abrv: data.sport.abrv,
			name: data.sport.name,
			count: data.eventCount,
			children: [createCategory(data)],
		};
	};

	const orderedResults = orderBy(events, [
		"sport.sortOrder",
		"sport.id",
		"sportCategory.sortOrder",
		"sportCategory.id",
		"tournament.sortOrder",
		"tournament.id",
	]);

	const formattedResult = orderedResults.reduce<ResultsMenuSport[]>(
		(acc, item) => {
			const sportIdx = acc.findIndex((s) => s.id === item.sport.id);

			if (sportIdx > -1) {
				const sport = acc[sportIdx];
				const sportCategoryIdx = sport.children
					? sport.children.findIndex(
							(c) => c.id === item.sportCategory.id
						)
					: -1;

				if (sportCategoryIdx > -1) {
					const category = sport.children[sportCategoryIdx];
					const tournamentIdx = category.children
						? category.children.findIndex(
								(t) => t.id === item.tournament.id
							)
						: -1;

					if (tournamentIdx > -1) {
						category.children[tournamentIdx].count +=
							item.eventCount;
						category.count += item.eventCount;
						sport.count += item.eventCount;
					} else {
						category.children.push(createTournament(item));
						category.count += item.eventCount;
						sport.count += item.eventCount;
					}
				} else {
					sport.children.push(createCategory(item));
					sport.count += item.eventCount;
				}
			} else {
				acc.push(createSport(item));
			}

			return acc;
		},
		[]
	);

	return formattedResult;
}

export function getOrderedEvents(
	events: EventWithResultAndBettingOffers[],
	filterTournamentIds: string[] | null
) {
	if (filterTournamentIds) {
		return [...events].sort(
			(a, b) =>
				filterTournamentIds.indexOf(a.event.tournament.id) -
				filterTournamentIds.indexOf(b.event.tournament.id)
		);
	} else {
		return orderBy(
			events,
			[
				"event.tournament.sport.sportSetting.sortOrder",
				"event.startTime",
			],
			["asc", "desc"]
		);
	}
}

export function makeSport(eventData: Event) {
	return {
		id: eventData.tournament.sport.id,
		name: eventData.tournament.sport.name,
		abrv: eventData.tournament.sport.abrv,
	};
}

export function makeCategory(eventData: Event) {
	return {
		id: eventData.tournament.sportCategory.id,
		name: eventData.tournament.sportCategory.name,
		abrv: eventData.tournament.sportCategory.abrv,
	};
}

export function findItemIndex<T>(
	arr: ResultsFormattedData<T>[],
	item: EventWithResultAndBettingOffers
) {
	return arr.findIndex(
		(r) =>
			r.sport.id === item.event.tournament.sport.id &&
			r.sportCategory.id == item.event.tournament.sportCategory.id
	);
}

export function makeMappedEvent(item: EventWithResultAndBettingOffers) {
	const eventData = item.event;
	const teamOne = eventData.teamOne;
	const teamTwo = eventData.teamTwo;
	const resultData = eventData.resultParsed;
	const pens = resultData.scorePerPeriod?.find(
		(item) => item.periodType === PeriodType.PEN
	);
	const bettingOffer = item.bettingOffers?.find((tip) => tip.feedType === 0);

	const filteredServiceId = eventData.externalServiceIds?.find(
		(x) => x.providerId === "BetRadarId"
	);
	let sourceData = null;

	if (filteredServiceId) {
		const extractedProviderId = filteredServiceId.ids[0]?.match(
			/(?!sr:match:)(\d+)(?=([+]?))/gm
		);
		sourceData = {
			providerId:
				extractedProviderId != null ? extractedProviderId[0] : null,
			providerName: filteredServiceId.providerId,
		};
	}

	const createEventName = (name: string, gender: number) => {
		return `${name}${
			gender === 1
				? ` ${localizationService.t("OFFER.GENDER.WOMEN")}`
				: ""
		}`;
	};

	const scorePerPeriodFormatted = resultData.scorePerPeriod
		? resultData.scorePerPeriod
				.filter(
					(item) =>
						item.periodType !== PeriodType.OT &&
						item.periodType !== PeriodType.PEN
				)
				.map((item) => {
					return {
						home: item.home,
						away: item.away,
						name: item.name,
					};
				})
		: null;

	const mappedEvent: MappedEvent = {
		id: eventData.id,
		teamOneId: teamOne.id,
		teamOneName: teamOne.name,
		teamTwoId: teamTwo.id,
		teamTwoName: teamTwo.name,
		startTime: eventData.startTime,
		source: sourceData,
		tournamentGroupId: eventData.tournamentGroupId,
		teamOne: createEventName(teamOne.name, teamOne.gender),
		teamTwo: createEventName(teamTwo.name, teamTwo.gender),
		matchStatus: {
			name: eventData.matchStatus?.name,
			abrv: eventData.matchStatus?.abrv,
		},
		tournament: {
			sport: {
				abrv: eventData.tournament?.sport?.abrv,
			},
		},
		fullTime: resultData.fullTimeScore
			? {
					home: resultData.fullTimeScore.home,
					away: resultData.fullTimeScore.away,
				}
			: null,
		halfTime: resultData.halfTimeScore
			? {
					home: resultData.halfTimeScore.home,
					away: resultData.halfTimeScore.away,
				}
			: null,
		scorePerPeriod: scorePerPeriodFormatted,
		penalties: pens
			? { home: pens.home, away: pens.away, name: pens.name }
			: null,
		overTimeScore: resultData.overTimeScore
			? {
					home: resultData.overTimeScore.home,
					away: resultData.overTimeScore.away,
				}
			: null,
		tip: bettingOffer ? bettingOffer.bettingTypeTip : "-",
		odd: bettingOffer ? `${bettingOffer.providerValue}` : "-",
	};

	return mappedEvent;
}

export function makeFinalItemModel<TournamentData>(
	sport: ResultsFormattedItem,
	sportCategory: ResultsFormattedItem,
	tournament: ResultsFormattedTournament<TournamentData>
) {
	const itemFinalModel: ResultsFormattedData<TournamentData> = {
		sport,
		sportCategory,
		tournaments: [tournament],
		columnsCount: 0,
	};

	switch (sport.abrv) {
		case SportAbrv.TENNIS:
		case SportAbrv.BADMINTON:
		case SportAbrv.VOLLEYBALL:
		case SportAbrv.BEACH_VOLLEY:
		case SportAbrv.AUSSIE_RULES:
		case SportAbrv.FIELD_HOCKEY:
		case SportAbrv.AMERICAN_FOOTBALL:
			itemFinalModel.scoreType = ScoreType.PERPERIOD;
			break;

		case SportAbrv.DARTS:
		case SportAbrv.FORMULA_1:
		case SportAbrv.BIKES:
		case SportAbrv.BOXING:
		case SportAbrv.MMA:
		case SportAbrv.CYCLING:
		case SportAbrv.CRICKET:
		case SportAbrv.TABLE_TENNIS:
		case SportAbrv.BOWLS:
		case SportAbrv.SQUASH:
		case SportAbrv.SNOOKER:
		case SportAbrv.STOCK_CAR_RACING:
			itemFinalModel.scoreType = ScoreType.FTONLY;
			break;

		case SportAbrv.BASEBALL:
			itemFinalModel.scoreType = ScoreType.FTONLYWITHOT;
			break;

		case SportAbrv.ICE_HOCKEY:
		case SportAbrv.WATERPOLO:
		case SportAbrv.BASKETBALL:
		case SportAbrv.FLOORBALL:
			itemFinalModel.scoreType = ScoreType.PERPERIODWITHOT;
			break;

		case SportAbrv.RUGBY:
		case SportAbrv.SOCCER:
		case SportAbrv.FUTSAL:
		case SportAbrv.HANDBALL:
			itemFinalModel.scoreType = ScoreType.HEAD2HEADWITHOT;
			break;

		default:
			itemFinalModel.scoreType = ScoreType.HEAD2HEAD;
			break;
	}

	switch (sport.abrv) {
		case SportAbrv.ICE_HOCKEY:
			itemFinalModel.columnsCount = 3;
			break;

		case SportAbrv.AUSSIE_RULES:
		case SportAbrv.BASKETBALL:
		case SportAbrv.WATERPOLO:
		case SportAbrv.FIELD_HOCKEY:
		case SportAbrv.AMERICAN_FOOTBALL:
		case SportAbrv.FLOORBALL:
			itemFinalModel.columnsCount = 4;
			break;

		case SportAbrv.TENNIS:
		case SportAbrv.VOLLEYBALL:
		case SportAbrv.BEACH_VOLLEY:
		case SportAbrv.BADMINTON:
			itemFinalModel.columnsCount = 5;
			break;
	}

	return itemFinalModel;
}

function getProviderInfo(eventData: Event) {
	const filteredServiceId =
		eventData.tournamentSeason.externalServiceIds?.find(
			(x) => x.providerId === "BetRadarId"
		);

	if (filteredServiceId) {
		const providerId =
			filteredServiceId.ids[0]?.match(
				/(?!sr:season:)(\d+)(?=([+]?))/gm
			)?.[0] || null;
		return {
			providerId,
			providerName: filteredServiceId.providerId,
		};
	}
	return null;
}

export function makeDesktopTournament(
	eventData: Event,
	eventDate: string,
	mappedEvent: MappedEvent
) {
	const { id, name, abrv } = eventData.tournament;
	const providerInfo = getProviderInfo(eventData);
	const events = new Map<string, MappedEvent[]>();
	events.set(eventDate, [mappedEvent]);

	const tournamentEntry: ResultsFormattedTournament<
		Map<string, MappedEvent[]>
	> = {
		id,
		name,
		abrv,
		events,
	};

	if (providerInfo) {
		tournamentEntry.source = providerInfo;
	}

	return tournamentEntry;
}

export function makeMobileTournament(
	eventData: Event,
	mappedEvent: MappedEvent
) {
	const { id, name, abrv } = eventData.tournament;
	const providerInfo = getProviderInfo(eventData);

	const tournamentEntry: ResultsFormattedTournament<MappedEvent[]> = {
		id,
		name,
		abrv,
		events: [mappedEvent],
	};

	if (providerInfo) {
		tournamentEntry.source = providerInfo;
	}

	return tournamentEntry;
}
